/**
 * LLM-assisted clustering
 *
 * Suggests which items might be related for human review in item pools.
 * Registry: pool_clustering — embedded advisor. Transport: callFeatureLlm.
 */

import type { ClusterRelationType } from '../types/prioritization/itemClustering';
import { callFeatureLlm } from './llmClient';
import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

const CLUSTERING_SYSTEM_PROMPT =
  'You suggest which prioritization items might be duplicates or related. Respond with valid JSON only.';

export interface ItemSummary {
  id: string;
  title: string;
  description?: string;
}

export interface SuggestedClusterGroup {
  itemIds: string[];
  suggestedCanonicalId: string;
  relationType: ClusterRelationType;
  rationale: string;
  confidence: 'high' | 'medium' | 'low';
}

export interface SuggestClustersResult {
  suggestedGroups: SuggestedClusterGroup[];
  reasoning?: string;
}

/**
 * Ask the LLM to suggest which items might belong together (duplicates, variants, related).
 * Returns suggested groupings with suggested canonical item and relation type.
 */
export async function suggestClusters(items: ItemSummary[]): Promise<SuggestClustersResult> {
  if (!isAiCapabilityEnabled('pool_clustering') || items.length < 2) {
    return { suggestedGroups: [] };
  }

  try {
    const content = await callFeatureLlm({
      userPrompt: buildClusteringPrompt(items),
      systemPrompt: CLUSTERING_SYSTEM_PROMPT,
      conversationId: 'clustering-suggest-' + Date.now(),
      model: DEFAULT_MODEL,
      temperature: 0.3,
      maxTokens: 800,
    });
    if (!content) return { suggestedGroups: [] };
    return parseClusteringResponse(content);
  } catch (error) {
    console.warn('LLM clustering suggestion failed:', error);
    return { suggestedGroups: [] };
  }
}

function buildClusteringPrompt(items: ItemSummary[]): string {
  const list = items
    .map(
      (i, idx) =>
        `[${idx + 1}] id=${i.id}\n    title: ${i.title}\n    description: ${i.description ?? '(none)'}`
    )
    .join('\n\n');

  return `You are helping prioritize work items. Several people often submit the same or very similar requests with different wording. Your job is to suggest which items might be the same need (duplicates), variants of the same need, or related enough to group.

Given these items:

${list}

Suggest groups of items that could be clustered together. For each group:
- relationType: one of "duplicate" (same request, different wording), "variant" (same core need, different scope/approach), "related" (connected but distinct)
- Pick one item id as the suggested canonical (representative) for the group
- Provide a short rationale

Return JSON only, no markdown or extra text:
{
  "suggestedGroups": [
    {
      "itemIds": ["id1", "id2"],
      "suggestedCanonicalId": "id1",
      "relationType": "duplicate",
      "rationale": "Brief explanation",
      "confidence": "high" | "medium" | "low"
    }
  ],
  "reasoning": "Optional one sentence overall reasoning"
}

Only suggest groups where you see real overlap. Do not group unrelated items. If nothing seems to cluster, return {"suggestedGroups": [], "reasoning": "..."}.`;
}

function parseClusteringResponse(content: string): SuggestClustersResult {
  try {
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      const groups = Array.isArray(parsed.suggestedGroups) ? parsed.suggestedGroups : [];
      const valid: SuggestedClusterGroup[] = groups
        .filter(
          (g: unknown) =>
            g &&
            typeof g === 'object' &&
            Array.isArray((g as { itemIds?: unknown }).itemIds) &&
            (g as { itemIds: string[] }).itemIds.length >= 2 &&
            typeof (g as { suggestedCanonicalId?: unknown }).suggestedCanonicalId === 'string' &&
            typeof (g as { relationType?: unknown }).relationType === 'string' &&
            typeof (g as { rationale?: unknown }).rationale === 'string'
        )
        .map((g: Record<string, unknown>) => ({
          itemIds: g.itemIds as string[],
          suggestedCanonicalId: g.suggestedCanonicalId as string,
          relationType: validRelationType((g.relationType as string) ?? 'related'),
          rationale: (g.rationale as string) ?? '',
          confidence: validConfidence((g.confidence as string) ?? 'medium'),
        }));
      return {
        suggestedGroups: valid,
        reasoning: typeof parsed.reasoning === 'string' ? parsed.reasoning : undefined,
      };
    }
  } catch (e) {
    console.warn('Parse clustering response failed:', e);
  }
  return { suggestedGroups: [] };
}

function validRelationType(s: string): ClusterRelationType {
  const allowed: ClusterRelationType[] = ['duplicate', 'variant', 'related', 'decomposition', 'composition'];
  return allowed.includes(s as ClusterRelationType) ? (s as ClusterRelationType) : 'related';
}

function validConfidence(s: string): 'high' | 'medium' | 'low' {
  return s === 'high' || s === 'low' ? s : 'medium';
}
